import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { Organisation } from '~api';
import { api } from '~portal/api';
import { QuotaOverrideEditor } from '~portal/components/admin/QuotaOverrideEditor';

export const Route = createFileRoute('/_protected/admin/users_/organisations')({
  component: AdminOrganisationsPage,
});

function AdminOrganisationsPage() {
  const [selected, setSelected] = useState<Organisation.Summary | null>(null);
  const [pendingDetach, setPendingDetach] = useState<Organisation.Member | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The detach flow is WIP-disabled; the open handle returns when the button reactivates.
  const [detachOpened, { close: closeDetach }] = useDisclosure(false);

  const organisationsQuery = api.organisations.list.useQuery();

  const membersQuery = api.organisations.members.useQuery(
    { organisationId: selected?.id ?? '' },
    { enabled: Boolean(selected) },
  );

  const removeMemberMutation = api.organisations.removeMember.useMutation({
    onSuccess: () => {
      closeDetach();
      setPendingDetach(null);
      setError(null);
      membersQuery.refetch();
    },
    onError: (err) => {
      closeDetach();
      setPendingDetach(null);
      setError(err.message);
    },
  });

  const organisations = organisationsQuery.data ?? [];
  const members = membersQuery.data ?? [];

  return (
    <Stack gap="lg" p="lg">
      <Modal
        opened={detachOpened}
        onClose={() => {
          closeDetach();
          setPendingDetach(null);
        }}
        title="Confirm detach"
        centered
      >
        <Stack gap="md">
          <Text>
            Remove{' '}
            <Text span fw={600}>
              {pendingDetach?.email || pendingDetach?.workosUserId}
            </Text>{' '}
            from {selected?.name}? Their membership and its roles are removed, so they lose all
            product access on their next token. Re-onboarding is via a new signup or internal
            invitation — there is no manual reattach.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                closeDetach();
                setPendingDetach(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={removeMemberMutation.isPending}
              onClick={() => {
                if (!pendingDetach) return;
                removeMemberMutation.mutate({ membershipId: pendingDetach.membershipId });
              }}
            >
              Detach
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Title order={1}>Organisations</Title>

      {error && (
        <Alert color="red" title="Error" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper withBorder p="md">
        <Stack gap="md">
          <Title order={3}>Create Organisation</Title>

          {/* WIP-disabled, untested flow: inputs are readOnly (not disabled) and the button uses
              data-disabled so the pointer events the tooltips need survive. */}
          <Tooltip label="WIP">
            <TextInput label="Name" placeholder="e.g. Acme Corp" required value="" readOnly />
          </Tooltip>

          <Tooltip label="WIP">
            <TextInput
              label="Subscriber external ID"
              description="Internal subscriber ID mirrored into WorkOS as the organisation's external ID"
              placeholder="e.g. sub_acme"
              value=""
              readOnly
            />
          </Tooltip>

          <Group>
            <Tooltip label="WIP">
              <Button
                data-disabled
                onClick={(event) => event.preventDefault()}
                leftSection={<IconPlus size={16} />}
              >
                Create
              </Button>
            </Tooltip>
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Group justify="space-between" mb="md">
          <Title order={3}>Organisations</Title>
          <Button
            onClick={() => organisationsQuery.refetch()}
            loading={organisationsQuery.isFetching}
            size="xs"
            variant="light"
          >
            Refresh
          </Button>
        </Group>

        {organisations.length === 0 && !organisationsQuery.isLoading ? (
          <Text c="dimmed" size="sm">
            No organisations found
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Organisation ID</Table.Th>
                  <Table.Th>External ID</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Members</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {organisations.map((org) => (
                  <Table.Tr key={org.id}>
                    <Table.Td>
                      <Text size="sm">{org.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Code>{org.id}</Code>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{org.externalId ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{new Date(org.createdAt).toLocaleDateString()}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant={selected?.id === org.id ? 'filled' : 'light'}
                        onClick={() => setSelected(org)}
                      >
                        Manage
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      {selected && (
        <Paper withBorder p="md" component="section" aria-label="Organisation quota limits">
          <Stack gap="md">
            <Title order={3}>Quota limits — {selected.name}</Title>
            <QuotaOverrideEditor
              key={selected.id}
              target={{ scope: 'org', subject: selected.id, label: selected.name }}
            />
          </Stack>
        </Paper>
      )}

      {selected && (
        <Paper withBorder p="md">
          <Stack gap="md">
            <Title order={3}>Members — {selected.name}</Title>

            {/* No attach flow: every user joins an organisation at onboarding time (signup or
                internal invitation), so membership is never assembled by hand here. */}
            {members.length === 0 && !membersQuery.isLoading ? (
              <Text c="dimmed" size="sm">
                No members in this organisation
              </Text>
            ) : (
              <ScrollArea>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Role</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {members.map((member) => (
                      <Table.Tr key={member.membershipId}>
                        <Table.Td>
                          <Text size="sm">{member.email || member.workosUserId}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{member.name ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{member.roles.join(', ') || '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={member.status === 'active' ? 'green' : 'gray'}
                            variant="filled"
                            size="sm"
                          >
                            {member.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {/* data-disabled, not disabled: a disabled button swallows the pointer
                              events the WIP tooltip needs. */}
                          <Tooltip label="WIP">
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              data-disabled
                              onClick={(event) => event.preventDefault()}
                            >
                              Detach
                            </Button>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
